import {
  validateEmail,
  validateName,
  validateCompany,
  validatePhone,
  validateUrl,
  validateTextArea,
  validateFeiNumber
} from '../utils/validations';
import { CDMO_STATUS } from '../constants';
import { isCanonicalSpeedToStart } from '../constants/scoring';
import { VALIDATION_MESSAGES } from '../constants/messages';
import type { CDMOApplication } from './supabase';

/**
 * `form` is every create and edit made through the profile form. `import` is the admin CSV bulk
 * upload, whose rows come from research that legitimately cannot find some facts: budgets are
 * rarely public and large CDMOs rarely publish an email. Import mode therefore skips exactly the
 * four "must be filled in" checks below and keeps every format and canonical-value check.
 */
export type CdmoApplicationValidationMode = 'form' | 'import';

export interface CdmoApplicationValidationOptions {
  mode?: CdmoApplicationValidationMode;
}

/**
 * Backend validation for a CDMO profile write. Lives outside `supabase.ts` so it can be unit-tested
 * (that module reads `import.meta.env`, which Jest cannot load); `validationService` delegates here.
 */
export function validateCdmoApplicationData(
  data: Partial<CDMOApplication>,
  options: CdmoApplicationValidationOptions = {}
): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];
  const isImport = options.mode === 'import';

  const isClaimed = data?.status === CDMO_STATUS.CLAIMED;
  const isUnclaimed = data?.status === CDMO_STATUS.UNCLAIMED;

  // Validate company name
  const companyValidation = validateCompany(data?.company_name || '');
  if (!companyValidation.isValid) {
    errors.push(companyValidation.error!);
  }

  // Validate contact name (Mandatory if claimed)
  if (isClaimed) {
    const nameValidation = validateName(data?.contact_name || '');
    if (!nameValidation.isValid) {
      errors.push(nameValidation.error!);
    }
  }

  // Validate email (Mandatory if claimed)
  if (isClaimed) {
    const emailValidation = validateEmail(data?.email || '');
    if (!emailValidation.isValid) {
      errors.push(emailValidation.error!);
    }
  }

  // Validate primary_email (Mandatory if unclaimed). An import may leave it blank, but a value that
  // is present must still be a valid address.
  if (isUnclaimed && (!isImport || data?.primary_email)) {
    const primaryEmailValidation = validateEmail(data?.primary_email || '');
    if (!primaryEmailValidation.isValid) {
      errors.push(`Primary contact email: ${primaryEmailValidation.error}`);
    }
  }

  // Validate phone (Mandatory if claimed)
  if (isClaimed) {
    const phoneValidation = validatePhone(data?.phone || '');
    if (!phoneValidation.isValid) {
      errors.push(phoneValidation.error!);
    }
  }

  // Validate website (optional)
  if (data.website) {
    const urlValidation = validateUrl(data.website);
    if (!urlValidation.isValid) {
      errors.push(urlValidation.error!);
    }
  }

  // Validate FEI numbers (optional, stored as comma-separated string)
  if (data?.fei_number) {
    const feiArr = data.fei_number.split(',').map(s => s.trim()).filter(Boolean);
    feiArr.forEach(fei => {
      const feiValidation = validateFeiNumber(fei);
      if (!feiValidation.isValid) {
        errors.push(feiValidation.error!);
      }
    });
  }

  // Speed to start must stay one of the canonical options, otherwise the matching engine
  // cannot interpret it and silently scores the dimension as zero.
  if (data?.speed_to_start && !isCanonicalSpeedToStart(data.speed_to_start)) {
    errors.push(VALIDATION_MESSAGES.SPEED_TO_START_INVALID);
  }

  // Validate facility locations details (optional)
  if (data.facility_locations_details) {
    const textValidation = validateTextArea(
      data.facility_locations_details,
      'Facility locations details',
      10,
      2000,
      false
    );
    if (!textValidation.isValid) {
      errors.push(textValidation.error!);
    }
  }

  // Validate required arrays
  if (!data?.primary_capabilities || data?.primary_capabilities?.length === 0) {
    errors.push(VALIDATION_MESSAGES.PRIMARY_MODALITY_REQUIRED);
  }

  if (!isImport && (!data?.typical_batch_sizes || data?.typical_batch_sizes?.length === 0)) {
    errors.push('Please select at least one typical batch size');
  }

  if (!isImport && (!data?.budget_ranges || data?.budget_ranges?.length === 0)) {
    errors.push(VALIDATION_MESSAGES.BUDGET_RANGE_REQUIRED);
  }

  if (!isImport && (!data?.operating_regions || data?.operating_regions?.length === 0)) {
    errors.push(VALIDATION_MESSAGES.OPERATING_REGION_REQUIRED);
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}
